import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, GraduationCap, Pill, AlertOctagon, ClipboardCheck, MessageSquareWarning,
  Target, Shield, ShieldAlert, RefreshCw, TrendingUp, TrendingDown, CheckCircle2, Clock,
} from "lucide-react";

const ICON_MAP = {
  graduation: GraduationCap,
  pill: Pill,
  alert: AlertOctagon,
  clipboard: ClipboardCheck,
  message: MessageSquareWarning,
  target: Target,
  shield: Shield,
};

const PRIORITY_META = {
  high: { cls: "border-red-300 text-red-800 bg-red-50 dark:bg-red-950/30", dot: "bg-red-500" },
  medium: { cls: "border-amber-300 text-amber-800 bg-amber-50 dark:bg-amber-950/30", dot: "bg-amber-500" },
  low: { cls: "border-emerald-300 text-emerald-800 bg-emerald-50 dark:bg-emerald-950/30", dot: "bg-emerald-500" },
};

/** Return "Good morning|afternoon|evening" from the user's LOCAL browser hour. */
function greetingForNow(now = new Date()) {
  const h = now.getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  return "Good evening"; // 17:00–04:59
}

/** Strip any leading "Good morning/afternoon/evening" (with optional comma / name)
 * from the AI-generated greeting so we can prepend the local-time version.
 * Falls back to using the manager's name if the AI didn't include one. */
function personalisedGreeting(aiGreeting, fallbackName) {
  const local = greetingForNow();
  if (aiGreeting) {
    // e.g. "Good morning, Sarah." → capture the "Sarah" (or nothing)
    const m = aiGreeting.match(/^Good (?:morning|afternoon|evening|day|night)\s*[,.-]?\s*(.*)$/i);
    const rest = m ? m[1].trim() : aiGreeting.trim();
    if (rest) return `${local}, ${rest.replace(/[.,\s]+$/, "")}.`;
  }
  return fallbackName ? `${local}, ${fallbackName}.` : `${local}.`;
}

export default function AiExecutiveBrief() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nowTick, setNowTick] = useState(0);
  const { user } = useAuth() || {};

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ai/exec-brief");
      setData(r.data);
    } catch (e) { /* silent — dashboard remains functional */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Refresh greeting every 5 min so the tab left open through a time-of-day change updates itself.
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) {
    return (
      <div className="bg-gradient-to-br from-violet-50 to-sky-50 dark:from-violet-950/30 dark:to-sky-950/30 border border-violet-200 dark:border-violet-800 rounded-lg p-5 animate-pulse" data-testid="exec-brief-loading">
        <div className="text-xs text-violet-700 dark:text-violet-300 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Preparing your daily briefing…
        </div>
      </div>
    );
  }
  if (!data) return null;

  // Insufficient-evidence baseline (new tenants) — never show a % or claim
  // "running smoothly" when the underlying collections are empty.
  const readinessStatus = data.kpis?.readiness_status || data.readiness_status;
  if (readinessStatus === "insufficient_evidence") {
    const checklist = data.kpis?.setup_checklist || data.setup_checklist || [];
    const remaining = checklist.filter((c) => !c.done);
    return (
      <div
        data-testid="exec-brief-insufficient-evidence"
        className="bg-gradient-to-br from-sky-50 to-emerald-50 dark:from-sky-950/30 dark:to-emerald-950/30 border border-sky-200 dark:border-sky-800 rounded-lg p-6"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-sky-700 dark:text-sky-300 flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4" /> Welcome to AYOBELL
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-slate-100 mb-2" data-testid="exec-brief-welcome-heading">
              {personalisedGreeting(data?.narrative?.greeting, user?.full_name || user?.name)}
            </h2>
            <p className="text-sm text-slate-700 dark:text-slate-300 max-w-2xl">
              Your organisation has been successfully created. Complete your initial
              compliance setup so AYOBELL can establish your first inspection-readiness
              baseline.
            </p>
          </div>
          <div
            className="shrink-0 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center min-w-[180px]"
            data-testid="exec-readiness-not-assessed"
          >
            <div className="text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-400">CQC Readiness Assessment</div>
            <div className="text-lg font-heading font-semibold text-slate-800 dark:text-slate-200 mt-1">Not Yet Available</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Insufficient evidence</div>
          </div>
        </div>

        {remaining.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400 mb-2">Initial setup checklist</div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="exec-brief-setup-checklist">
              {checklist.map((c) => (
                <li key={c.key} className="flex items-start gap-2 text-sm">
                  {c.done ? (
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                  ) : (
                    <Clock className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  )}
                  <span className={c.done ? "text-slate-500 line-through" : "text-slate-800 dark:text-slate-200"}>
                    {c.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  const readiness = data.kpis?.cqc_readiness_pct;
  const readinessColour = readiness >= 80 ? "text-emerald-700" : readiness >= 60 ? "text-amber-700" : "text-red-700";
  const readinessBg = readiness >= 80 ? "bg-emerald-100" : readiness >= 60 ? "bg-amber-100" : "bg-red-100";
  const delta = data.kpis?.training_delta;

  return (
    <div className="bg-gradient-to-br from-white to-violet-50/40 dark:from-slate-900 dark:to-violet-950/20 border border-violet-200 dark:border-violet-800 rounded-lg p-5" data-testid="exec-brief">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-700 dark:text-violet-300 font-semibold mb-1">
            <Sparkles className="w-3 h-3" /> Ayobell Premium · Executive AI Briefing
          </div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100" data-testid="exec-greeting">
            {personalisedGreeting(data.greeting, user?.name || (user?.email || "").split("@")[0])}
          </h2>
          {data.summary && <p className="text-sm text-slate-700 dark:text-slate-200 mt-1" data-testid="exec-summary">{data.summary}</p>}
          {data.wins?.length > 0 && (
            <ul className="mt-2 text-sm text-emerald-700 dark:text-emerald-300 space-y-0.5" data-testid="exec-wins">
              {data.wins.map((w, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {w}
                </li>
              ))}
            </ul>
          )}
        </div>
        {readiness != null && (
          <div className={`shrink-0 ${readinessBg} border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-center min-w-[120px]`} data-testid="exec-readiness">
            <div className="text-[9px] uppercase tracking-widest text-slate-600 font-semibold">Estimated CQC Readiness</div>
            <div className={`text-3xl font-heading font-bold tabular-nums ${readinessColour}`}>{Math.round(readiness)}%</div>
          </div>
        )}
      </div>

      {/* Quick KPIs row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-testid="exec-kpis">
        <KpiTile
          icon={GraduationCap}
          label="Training Compliance"
          value={`${data.kpis?.training_compliance_pct || 0}%`}
          delta={delta}
          tone="info"
          testid="kpi-training"
        />
        <KpiTile icon={Pill} label="Medication Reviews Overdue" value={data.kpis?.med_reviews_overdue ?? 0} tone={data.kpis?.med_reviews_overdue > 0 ? "warn" : "ok"} testid="kpi-med-reviews" />
        <KpiTile icon={AlertOctagon} label="Open Incidents" value={data.kpis?.incidents_open ?? 0} tone={data.kpis?.incidents_open > 0 ? "warn" : "ok"} testid="kpi-incidents" />
        <KpiTile icon={ShieldAlert} label="Safeguarding Open" value={data.kpis?.safeguarding_open ?? 0} tone={data.kpis?.safeguarding_open > 0 ? "danger" : "ok"} testid="kpi-safeguarding" />
      </div>

      {/* Priority actions */}
      {data.priority_actions?.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
            <Target className="w-3 h-3" /> Priority actions
          </div>
          <ul className="space-y-1.5" data-testid="exec-priority-actions">
            {data.priority_actions.slice(0, 5).map((a, i) => {
              const Icon = ICON_MAP[a.icon] || Target;
              const meta = PRIORITY_META[a.priority] || PRIORITY_META.medium;
              return (
                <li key={i} className={`flex items-center gap-3 px-3 py-2 border ${meta.cls} rounded-md text-sm`} data-testid={`priority-action-${i}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{a.title}</span>
                  <span className="text-[10px] uppercase tracking-widest font-semibold">{a.priority}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
        <span>Generated {new Date(data.generated_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
        <button onClick={load} className="inline-flex items-center gap-1 hover:text-violet-700" data-testid="exec-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh briefing
        </button>
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, delta, tone = "info", testid }) {
  const tones = {
    ok: "border-emerald-200 bg-white text-emerald-800",
    warn: "border-amber-200 bg-white text-amber-800",
    danger: "border-red-200 bg-white text-red-800",
    info: "border-slate-200 bg-white text-slate-800",
  };
  return (
    <div className={`border rounded-md p-2 flex items-center gap-2 ${tones[tone]} dark:bg-slate-900`} data-testid={testid}>
      <Icon className="w-4 h-4 shrink-0 opacity-70" />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-widest opacity-75 font-semibold truncate">{label}</div>
        <div className="text-base font-heading font-semibold tabular-nums leading-tight flex items-center gap-1">
          {value}
          {delta != null && delta !== 0 && (
            <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${delta > 0 ? "text-emerald-700" : "text-red-700"}`}>
              {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {delta > 0 ? "+" : ""}{delta}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
